export type CodeExecutionKind = "shell" | "python" | "code";

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const normalizeWhitespace = (value: string): string => value.replaceAll(/\s+/g, " ").trim();

export const truncateText = (value: string, maxLength = 52): string =>
  value.length <= maxLength ? value : `${value.slice(0, maxLength - 1)}…`;

export const parseToolResult = (result: unknown): Record<string, unknown> | null => {
  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result) as unknown;
      return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return typeof result === "object" && result !== null ? (result as Record<string, unknown>) : null;
};

const getExitCode = (result: Record<string, unknown>): number | null => {
  const exitCode = result.exit_code ?? result.exitCode;
  return typeof exitCode === "number" ? exitCode : null;
};

export const isFailedToolResult = (result: Record<string, unknown> | null): boolean => {
  if (!result) return false;
  if (typeof result.error === "string" && result.error.length > 0) return true;
  const exitCode = getExitCode(result);
  if (exitCode !== null) return exitCode !== 0;
  return false;
};

const SHELL_CODE_LANGUAGES = new Set(["bash", "shell", "sh", "zsh"]);
const PYTHON_CODE_LANGUAGES = new Set(["python", "py"]);

const getExplicitCodeLanguage = (parameters?: Record<string, unknown>): string | null =>
  typeof parameters?.language === "string" ? parameters.language.toLowerCase() : null;

export const isCodeExecutionTool = (functionName: string): boolean =>
  functionName.includes("CodeExecutionPlugin") ||
  functionName.includes("execute_python") ||
  functionName.includes("execute_command") ||
  functionName.includes("execute_code");

export const getCodeExecutionKind = (functionName: string, parameters?: Record<string, unknown>): CodeExecutionKind => {
  if (functionName.includes("execute_python")) return "python";
  if (functionName.includes("execute_command")) return "shell";
  if (functionName.includes("execute_code") || functionName.includes("CodeExecutionPlugin")) {
    const language = getExplicitCodeLanguage(parameters);
    if (!language || PYTHON_CODE_LANGUAGES.has(language)) return "python";
    if (SHELL_CODE_LANGUAGES.has(language)) return "shell";
    return "code";
  }
  return "shell";
};

export const isPythonExecutionTool = (functionName: string, parameters?: Record<string, unknown>): boolean =>
  getCodeExecutionKind(functionName, parameters) === "python";

export const inferCommandSummary = (command: string, t: TranslateFn): string => {
  const normalized = normalizeWhitespace(command);
  const firstToken = /^[^\s|&;]+/.exec(normalized)?.[0]?.toLowerCase() ?? "";
  if (["sed", "cat", "head", "tail"].includes(firstToken)) return t("tools.phrases.readFiles");
  if (["find", "ls", "tree"].includes(firstToken)) {
    return normalized.includes("/uploads") ? t("tools.phrases.listedUploads") : t("tools.phrases.listedFiles");
  }
  if (["rg", "grep"].includes(firstToken)) return t("tools.phrases.searchedFiles");
  if (firstToken === "pwd") return t("tools.phrases.checkedWorkingDirectory");
  return truncateText(normalized);
};

const extractParameterText = (parameters: Record<string, unknown> | undefined, aliases: readonly string[]): string | null => {
  if (!parameters) return null;
  for (const key of aliases) {
    const value = parameters[key];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }
  return null;
};

const SHELL_ALIASES = ["command", "cmd", "shell", "bash", "code", "script"] as const;
const PYTHON_ALIASES = ["script", "code", "source", "program"] as const;
const CODE_ALIASES = ["code", "script", "source", "program"] as const;

export const extractShellCommand = (parameters?: Record<string, unknown>): string | null =>
  extractParameterText(parameters, SHELL_ALIASES);

export const extractPythonScript = (parameters?: Record<string, unknown>): string | null =>
  extractParameterText(parameters, PYTHON_ALIASES);

export const extractCodePayload = (parameters?: Record<string, unknown>): string | null =>
  extractParameterText(parameters, CODE_ALIASES);

export const inferPythonSummary = (script: string, t: TranslateFn): string | undefined => {
  const firstMeaningfulLine = script
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (!firstMeaningfulLine) return undefined;
  if (firstMeaningfulLine.includes("PdfReader") || firstMeaningfulLine.includes("pypdf")) return t("tools.phrases.readPdf");
  return truncateText(firstMeaningfulLine);
};

export const extractExecutionOutput = (result: Record<string, unknown> | null): string | null => {
  if (!result) return null;
  if (typeof result.error === "string" && result.error.trim()) return result.error.trim();
  const chunks: string[] = [];
  const stderr = typeof result.stderr === "string" ? result.stderr.trim() : "";
  const stdout = typeof result.stdout === "string" ? result.stdout.trim() : "";
  if (stderr) chunks.push(stderr);
  if (stdout) chunks.push(stdout);
  return chunks.length > 0 ? chunks.join("\n\n") : null;
};

export function asToolParameters(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

/** First-line subtitle for a code tool, using the command or script the model actually sent. */
export function codeExecutionSubtitle(name: string, input: unknown, completed: boolean, t: TranslateFn): string {
  const parameters = asToolParameters(input);
  const kind = getCodeExecutionKind(name, parameters);
  if (kind === "python") {
    const script = extractPythonScript(parameters);
    if (script) return inferPythonSummary(script, t) ?? t(completed ? "tools.phrases.ranPython" : "tools.phrases.runningPython");
    return t(completed ? "tools.phrases.ranPython" : "tools.phrases.runningPython");
  }
  if (kind === "shell") {
    const command = extractShellCommand(parameters);
    if (command) return inferCommandSummary(command, t);
    return t(completed ? "tools.phrases.ranShellCommand" : "tools.phrases.runningShellCommand");
  }
  const payload = extractCodePayload(parameters);
  if (payload) {
    const line = payload.split(/\r?\n/u).map((item) => item.trim()).find((item) => item.length > 0);
    if (line) return truncateText(normalizeWhitespace(line));
  }
  return t(completed ? "tools.phrases.ranCode" : "tools.phrases.runningCode");
}

export function codeExecutionBody(name: string, input: unknown): string | null {
  const parameters = asToolParameters(input);
  const kind = getCodeExecutionKind(name, parameters);
  if (kind === "python") return extractPythonScript(parameters);
  if (kind === "shell") return extractShellCommand(parameters);
  return extractCodePayload(parameters);
}
